import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Heart,
  Home,
  Car,
  Users,
  Clock,
  Phone,
  Mail,
  MapPin,
  ChevronDown,
  Menu,
  X,
  Shield,
  Award,
  CheckCircle2,
  Star,
  Sparkles,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets
const ASSETS = {
  heroMain: "https://customer-assets.emergentagent.com/job_mckenzie-care/artifacts/9vqfn2wv_C7AC7E80-38F1-404B-A569-B80D1FDB3ADD.png",
  aboutCouple: "https://customer-assets.emergentagent.com/job_mckenzie-care/artifacts/9j1xhmh1_235BA799-0E72-4805-B5C9-C7B606B6DE46.png",
  careHands: "https://customer-assets.emergentagent.com/job_tascare-home/artifacts/zc78x6jp_IMG_6943.jpeg",
  headshot: "https://customer-assets.emergentagent.com/job_tascare-home/artifacts/zfifci4z_6791761D-0EB6-40F8-8188-CAAAAA0D4B03.png",
  logo: "https://customer-assets.emergentagent.com/job_4c9d74cf-725e-44af-a8db-6a22c3787313/artifacts/iqxluip5_IMG_6240.jpeg",
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#about", label: "About" },
    { href: "#services", label: "Services" },
    { href: "#pricing", label: "Pricing" },
    { href: "#faq", label: "FAQs" },
    { href: "/how-it-works", label: "How It Works", isPage: true },
  ];

  const handleNavClick = (link) => {
    if (link.isPage) {
      navigate(link.href);
    } else {
      // If we're not on the home page, navigate there first
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const element = document.querySelector(link.href);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        const element = document.querySelector(link.href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
    setMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" data-testid="logo-link" className="flex items-center">
            <img
              src={ASSETS.logo}
              alt="McKenzie Private Care"
              className="h-14 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="nav-link font-manrope text-sm font-medium text-cedar-800 hover:text-sage-700 transition-colors"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Contact Button */}
          <div className="hidden md:block">
            <Button
              onClick={() => handleNavClick({ href: "#contact" })}
              data-testid="nav-contact-btn"
              className="bg-sage-700 hover:bg-sage-800 text-white rounded-full px-6 py-2 font-manrope font-medium sage-pulse"
            >
              Get in Touch
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-cedar-800" />
            ) : (
              <Menu className="w-6 h-6 text-cedar-800" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-border"
          >
            <nav className="flex flex-col p-6 gap-4">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link)}
                  className="font-manrope text-lg font-medium text-cedar-800 hover:text-sage-700 text-left py-2"
                >
                  {link.label}
                </button>
              ))}
              <Button
                onClick={() => handleNavClick({ href: "#contact" })}
                className="bg-sage-700 hover:bg-sage-800 text-white rounded-full mt-2"
              >
                Get in Touch
              </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="pt-20 pb-12 md:pt-24 md:pb-20 hero-gradient">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 min-h-[70vh] md:min-h-[85vh]">
          {/* Main Hero Image */}
          <motion.div
            {...fadeInUp}
            className="bento-item md:col-span-8 relative rounded-3xl overflow-hidden group"
          >
            <img
              src={ASSETS.heroMain}
              alt="Caregiver providing compassionate support"
              className="w-full h-full object-cover min-h-[400px] md:min-h-full transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-cedar-800/90 via-cedar-800/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <span className="inline-block font-manrope text-xs md:text-sm tracking-widest uppercase text-kangaroo-300 mb-3">
                Private • Personal • Trusted
              </span>
              <h1 className="font-playfair text-3xl md:text-5xl lg:text-6xl text-white leading-tight mb-4">
                Respectful, Reliable,
                <br />
                Person-Centred Care
              </h1>
              <p className="font-manrope text-white/90 text-base md:text-lg max-w-xl mb-6">
                Professional in-home support tailored to your individual needs.
                Based between Devonport and Latrobe.
              </p>
              <Button
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-cta-btn"
                className="bg-sage-500 hover:bg-sage-600 text-white rounded-full px-8 py-6 text-lg font-manrope font-medium sage-pulse"
              >
                Enquire About Support
              </Button>
              <p className="font-manrope text-white/70 text-sm mt-4">
                Free, no-obligation consultation
              </p>
            </div>
          </motion.div>

          {/* Side Column */}
          <div className="md:col-span-4 flex flex-col gap-4 md:gap-6">
            {/* Care Hands Image */}
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.1 }}
              className="bento-item relative rounded-3xl overflow-hidden flex-1 min-h-[200px]"
            >
              <img
                src={ASSETS.careHands}
                alt="Professional care services"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="inline-flex items-center gap-2 bg-merino-200/95 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-manrope font-medium text-cedar-800">
                  <MapPin className="w-4 h-4" />
                  North West Coast
                </span>
              </div>
            </motion.div>

            {/* Olive Info Block */}
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.2 }}
              className="bento-item bg-sage-700 rounded-3xl p-6 md:p-8 flex-1 flex flex-col justify-center"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-kangaroo-400/30 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-kangaroo-300" />
                </div>
                <span className="font-manrope text-kangaroo-300 text-sm tracking-widest uppercase">
                  Flexible Hours
                </span>
              </div>
              <p className="font-playfair text-2xl md:text-3xl text-white mb-2">
                Up to 12-Hour
              </p>
              <p className="font-manrope text-white/80">
                Long respite shifts available for your peace of mind
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const qualifications = [
    "Certificate III in Individual Support",
    "First Aid and CPR",
    "Infection Prevention & Control",
    "Safe Food Handling & Mealtime Support",
    "Disability Awareness & Communication",
    "NDIS Worker Orientation",
    "Mental Health & Suicide Prevention (CORES)",
  ];

  const credentials = [
    { icon: Shield, label: "ABN Registered" },
    { icon: Award, label: "Fully Insured" },
    { icon: CheckCircle2, label: "Police Checked" },
    { icon: Star, label: "WWVP Card" },
  ];

  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src={ASSETS.aboutCouple}
                alt="Compassionate care moment"
                className="w-full h-auto object-cover"
              />
            </div>
            {/* Headshot card */}
            <div className="absolute -bottom-6 -right-6 md:bottom-8 md:-right-8 bg-white rounded-2xl shadow-xl p-3 max-w-[180px]">
              <img
                src={ASSETS.headshot}
                alt="McKenzie - Your Care Provider"
                className="w-full h-32 object-cover object-top rounded-xl mb-2"
              />
              <p className="font-playfair text-sm text-slate-700 text-center">
                McKenzie
              </p>
              <p className="font-manrope text-xs text-muted-foreground text-center">
                Your Care Provider
              </p>
            </div>
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-manrope text-sm tracking-widest uppercase text-sage-600 mb-4 block">
              About McKenzie
            </span>
            <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
              Dedicated to Compassionate, Quality Care
            </h2>
            <p className="font-manrope text-muted-foreground text-lg mb-8 leading-relaxed">
              I am a qualified support worker with experience across aged care,
              disability support, and childcare. I am committed to providing
              respectful, reliable, and person-centred care tailored to each
              individual's needs.
            </p>

            {/* Credentials */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {credentials.map((cred, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 bg-kangaroo-100 rounded-xl p-4"
                >
                  <div className="w-10 h-10 rounded-full bg-sage-500/20 flex items-center justify-center">
                    <cred.icon className="w-5 h-5 text-sage-600" />
                  </div>
                  <span className="font-manrope text-sm font-medium text-cedar-800">
                    {cred.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Qualifications */}
            <div className="bg-white border border-kangaroo-200 rounded-2xl p-6">
              <h3 className="font-playfair text-xl mb-4 text-cedar-800">
                Qualifications & Training
              </h3>
              <ul className="space-y-2">
                {qualifications.map((qual, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 font-manrope text-cedar-700"
                  >
                    <CheckCircle2 className="w-5 h-5 text-sage-500 flex-shrink-0 mt-0.5" />
                    <span>{qual}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// What Sets Us Apart Section
const WhySectionApart = () => {
  const differentiators = [
    {
      icon: Users,
      title: "One Carer, Every Visit",
      description:
        "No rotating staff or unfamiliar faces. You'll receive consistent care from the same person who understands your routines and preferences.",
    },
    {
      icon: Clock,
      title: "Flexible Scheduling",
      description:
        "Not locked into rigid agency timeframes. I work around your life, not the other way around – including extended shifts when you need them.",
    },
    {
      icon: Heart,
      title: "Genuinely Personalised",
      description:
        "Your care plan is built around you, not a template. Every detail is tailored to your unique needs, goals, and preferences.",
    },
    {
      icon: Star,
      title: "Limited Client List",
      description:
        "To ensure every client receives the attentive, quality care they deserve, I intentionally keep my client list small. Quality over quantity, always.",
    },
  ];

  return (
    <section id="why-mpc" data-testid="why-mpc-section" className="py-20 md:py-32 bg-sage-700">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="font-manrope text-sm tracking-widest uppercase text-kangaroo-400 mb-4 block">
            The MPC Difference
          </span>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-white mb-6">
            What Sets Us Apart
          </h2>
          <p className="font-manrope text-white/80 text-lg max-w-2xl mx-auto">
            When you choose McKenzie Private Care, you're choosing a genuine partner in your care journey.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
        >
          {differentiators.map((item, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              data-testid={`differentiator-${index}`}
              className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:bg-white/15 transition-colors duration-300"
            >
              <div className="w-14 h-14 rounded-2xl bg-kangaroo-400/20 flex items-center justify-center mb-6">
                <item.icon className="w-7 h-7 text-kangaroo-300" />
              </div>
              <h3 className="font-playfair text-xl md:text-2xl text-white mb-3">
                {item.title}
              </h3>
              <p className="font-manrope text-white/80 leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Provider Disclosure */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 bg-white/5 rounded-2xl p-6 md:p-8 border border-white/10"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-kangaroo-400/20 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-kangaroo-300" />
            </div>
            <div>
              <h4 className="font-playfair text-lg text-white mb-2">
                Independent Private Provider
              </h4>
              <p className="font-manrope text-white/70 text-sm leading-relaxed">
                McKenzie Private Care operates as an independent private provider, supporting private clients, self-managed Home Care Packages, and NDIS participants with self-managed funding. This means greater choice, flexibility, and continuity of care for you and your family.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Heart,
      title: "Personal Care",
      description:
        "Assistance with daily living activities including bathing, dressing, grooming, and mobility support with dignity and respect.",
    },
    {
      icon: Home,
      title: "Domestic Assistance",
      description:
        "Help with household tasks such as cleaning, laundry, meal preparation, and maintaining a safe, comfortable home environment.",
    },
    {
      icon: Car,
      title: "Transport & Community",
      description:
        "Safe transport to appointments, shopping trips, social outings, and community activities to maintain independence.",
    },
    {
      icon: Users,
      title: "Social Support",
      description:
        "Companionship and engagement through conversation, activities, and outings to support emotional wellbeing.",
    },
    {
      icon: Clock,
      title: "Respite Care",
      description:
        "Extended shifts up to 12 hours to provide family carers with a well-deserved break while ensuring continuity of care.",
    },
    {
      icon: Sparkles,
      title: "Flexible Support",
      description:
        "Tailored care plans that adapt to your changing needs, whether you're private, HCP, or NDIS self-managed.",
    },
  ];

  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-merino-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="font-manrope text-sm tracking-widest uppercase text-sage-600 mb-4 block">
            Our Services
          </span>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-cedar-800 mb-6">
            Comprehensive In-Home Support
          </h2>
          <p className="font-manrope text-cedar-600 text-lg max-w-2xl mx-auto">
            Flexible, one-on-one care services designed to support your
            independence and enhance your quality of life.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              data-testid={`service-card-${index}`}
              className="service-card group relative overflow-hidden rounded-3xl bg-white p-8 border border-kangaroo-200 hover:border-sage-400"
            >
              <div className="w-14 h-14 rounded-2xl bg-sage-500/10 flex items-center justify-center mb-6 group-hover:bg-sage-500 transition-colors duration-300">
                <service.icon className="w-7 h-7 text-sage-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-playfair text-xl md:text-2xl mb-3 text-cedar-800">
                {service.title}
              </h3>
              <p className="font-manrope text-cedar-600 leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  return (
    <section id="pricing" data-testid="pricing-section" className="py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="font-manrope text-sm tracking-widest uppercase text-sage-600 mb-4 block">
            Transparent Pricing
          </span>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-cedar-800 mb-6">
            Transparent, Fair Rates
          </h2>
          <p className="font-manrope text-cedar-600 text-lg max-w-2xl mx-auto">
            Quality care at competitive rates. I work with private clients,
            self-managed Home Care Packages, and self-managed NDIS supports.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Hourly Rate Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            data-testid="pricing-hourly"
            className="relative rounded-3xl bg-white border-2 border-sage-700 p-8 md:p-10"
          >
            <div className="absolute -top-4 left-8">
              <span className="bg-sage-700 text-white font-manrope text-xs tracking-widest uppercase px-4 py-2 rounded-full">
                Base Rate
              </span>
            </div>
            <div className="pt-4">
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-playfair text-5xl md:text-6xl text-sage-700">
                  $85
                </span>
                <span className="font-playfair text-3xl text-cedar-400">
                  -
                </span>
                <span className="font-playfair text-5xl md:text-6xl text-sage-700">
                  $95
                </span>
              </div>
              <p className="font-manrope text-cedar-600 mb-2">
                per hour (depending on service type)
              </p>
              <p className="font-manrope text-cedar-500 text-sm italic mb-6">
                Weekend, after-hours & public holiday rates apply.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 font-manrope text-cedar-700">
                  <CheckCircle2 className="w-5 h-5 text-sage-500" />
                  <span>Travel to and from client included</span>
                </li>
                <li className="flex items-center gap-3 font-manrope text-cedar-700">
                  <CheckCircle2 className="w-5 h-5 text-sage-500" />
                  <span>Flexible scheduling available</span>
                </li>
                <li className="flex items-center gap-3 font-manrope text-cedar-700">
                  <CheckCircle2 className="w-5 h-5 text-sage-500" />
                  <span>Extended shifts up to 12 hours available</span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Kilometre Rate Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            data-testid="pricing-km"
            className="relative rounded-3xl bg-kangaroo-100 border border-kangaroo-200 p-8 md:p-10"
          >
            <div className="absolute -top-4 left-8">
              <span className="bg-sage-500 text-white font-manrope text-xs tracking-widest uppercase px-4 py-2 rounded-full">
                In-Service Travel
              </span>
            </div>
            <div className="pt-4">
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-playfair text-5xl md:text-6xl text-sage-700">
                  $0.88
                </span>
                <span className="font-manrope text-xl text-cedar-600">
                  /km
                </span>
              </div>
              <p className="font-manrope text-cedar-600 mb-6">
                for in-service visits and outings
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 font-manrope text-cedar-700">
                  <Car className="w-5 h-5 text-sage-500" />
                  <span>Modern, reliable vehicle</span>
                </li>
                <li className="flex items-center gap-3 font-manrope text-cedar-700">
                  <Shield className="w-5 h-5 text-sage-500" />
                  <span>Fully insured for transport</span>
                </li>
                <li className="flex items-center gap-3 font-manrope text-cedar-700">
                  <MapPin className="w-5 h-5 text-sage-500" />
                  <span>Appointments & outings</span>
                </li>
                <li className="flex items-center gap-3 font-manrope text-cedar-700">
                  <CheckCircle2 className="w-5 h-5 text-sage-500" />
                  <span>Shopping trips included</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Accepted Payment Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <h3 className="font-playfair text-2xl text-cedar-800 mb-4">
            Accepted Payment Options
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <span className="bg-muted rounded-full px-5 py-2 font-manrope text-sm">
              Private (Out-of-pocket)
            </span>
            <span className="bg-muted rounded-full px-5 py-2 font-manrope text-sm">
              NDIS participants with self-managed funding
            </span>
            <span className="bg-muted rounded-full px-5 py-2 font-manrope text-sm">
              Self-managed Home Care Packages
            </span>
          </div>
        </motion.div>

        {/* NDIS Funding Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-8 max-w-3xl mx-auto"
        >
          <div className="bg-kangaroo-100 border border-kangaroo-200 rounded-2xl p-6">
            <h4 className="font-playfair text-lg text-cedar-800 mb-2">
              NDIS Funding Notice
            </h4>
            <p className="font-manrope text-cedar-600 text-sm leading-relaxed">
              MPC is an independent, non-registered provider working with self-managed NDIS participants. Services must align with your plan and available budget. NDIS reimburses up to the set price limits, and any difference is payable privately.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "Do you offer a free consultation?",
      answer:
        "Yes! I offer a free, no-obligation in-home consultation to discuss your care needs and see if we're the right fit for each other. This gives us both a chance to meet, ask questions, and ensure you feel comfortable before making any commitment.",
    },
    {
      question: "What areas do you service?",
      answer:
        "I am based between Devonport and Latrobe, with travel available across the North West Coast of Tasmania. My primary service area includes Shearwater to Penguin, but I'm willing to travel further for the right client offering good long hours.",
    },
    {
      question: "What qualifications do you hold?",
      answer:
        "I hold a Certificate III in Individual Support and maintain current training including First Aid and CPR, infection prevention and control, safe food handling, disability awareness, NDIS Worker Orientation, safeguarding obligations, and mental health awareness (CORES).",
    },
    {
      question: "Are you insured?",
      answer:
        "Yes, I am fully insured with both public liability and professional indemnity insurance. I am also ABN registered, hold a National Police Check, and have a current Working with Vulnerable People Card.",
    },
    {
      question: "What types of clients do you work with?",
      answer:
        "I work with private out-of-pocket clients, self-managed Home Care Packages, and NDIS participants with self-managed funding. As an independent private provider, I offer families greater choice, flexibility, and continuity of care.",
    },
    {
      question: "How long are your respite shifts?",
      answer:
        "I offer flexible respite care shifts of up to 12 hours, providing family carers with extended breaks while ensuring consistent, quality care for their loved ones.",
    },
    {
      question: "How do I book your services?",
      answer:
        "Simply fill out the contact form below or call me directly on 0456 786 124. I'm happy to discuss your needs and see if we're the right fit. There's no obligation – just a friendly chat to understand how I can best support you.",
    },
    {
      question: "What is included in the hourly rate?",
      answer:
        "The hourly rate of $85-$95 includes travel to and from your home. For any travel during your care session (such as appointments or outings), a kilometre rate of $0.88/km applies.",
    },
    {
      question: "Can you provide care for people with disabilities?",
      answer:
        "Yes, I have experience in disability support and hold disability awareness and effective communication training. I work with NDIS participants who have self-managed funding to provide person-centred care.",
    },
    {
      question: "Do you accept NDIS participants?",
      answer:
        "Yes. MPC supports participants with self-managed NDIS funding. As an independent private provider, our rates may differ from NDIS price limits, and any gap is payable privately.",
    },
  ];

  return (
    <section id="faq" data-testid="faq-section" className="py-20 md:py-32 bg-merino-200">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="font-manrope text-sm tracking-widest uppercase text-sage-600 mb-4 block">
            Questions & Answers
          </span>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-cedar-800 mb-6">
            Frequently Asked Questions
          </h2>
          <p className="font-manrope text-cedar-600 text-lg">
            Find answers to common questions about my services.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                data-testid={`faq-item-${index}`}
                className="bg-white rounded-2xl border border-border px-6 overflow-hidden"
              >
                <AccordionTrigger className="font-playfair text-lg text-left py-6 hover:text-walnut-600 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-manrope text-muted-foreground pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/inquiries`, formData);
      if (response.data) {
        toast.success("Thank you for your inquiry! McKenzie will be in touch soon.");
        setFormData({
          name: "",
          email: "",
          phone: "",
          service_type: "",
          message: "",
        });
      }
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Something went wrong. Please try calling directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="font-manrope text-sm tracking-widest uppercase text-sage-600 mb-4 block">
            Get in Touch
          </span>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-cedar-800 mb-6">
            Let's Talk About Support
          </h2>
          <p className="font-manrope text-cedar-600 text-lg max-w-2xl mx-auto">
            Ready to find out if we're the right fit? Fill out the form or reach
            out directly – I'd love to hear from you.
          </p>
          <div className="inline-flex items-center gap-2 mt-4 bg-kangaroo-200 text-cedar-700 rounded-full px-4 py-2">
            <CheckCircle2 className="w-4 h-4" />
            <span className="font-manrope text-sm font-medium">Free, no-obligation consultation</span>
          </div>
          <p className="font-manrope text-cedar-500 text-sm mt-4 italic">
            Please note: Due to existing commitments, new clients typically require 6 weeks notice to begin services.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Contact Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href="tel:0456786124"
                data-testid="contact-phone-link"
                className="flex items-center gap-4 bg-sage-500 text-white rounded-2xl p-6 hover:bg-sage-600 sage-glow"
              >
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-manrope text-sm text-white/80">Call me</p>
                  <p className="font-manrope font-medium">0456 786 124</p>
                </div>
              </a>
              <a
                href="mailto:mckenzieprivatecare@gmail.com"
                data-testid="contact-email-link"
                className="flex items-center gap-4 bg-sage-500 text-white rounded-2xl p-6 hover:bg-sage-600 sage-glow"
              >
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-manrope text-sm text-white/80">
                    Email me
                  </p>
                  <p className="font-manrope font-medium text-sm">
                    mckenzieprivatecare@gmail.com
                  </p>
                </div>
              </a>
            </div>

            {/* Service Area Map */}
            <div className="rounded-3xl overflow-hidden border border-border">
              <div className="bg-walnut-700 text-white p-4">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5" />
                  <span className="font-manrope font-medium">Service Area</span>
                </div>
              </div>
              <div className="relative h-[300px] md:h-[350px]">
                <iframe
                  title="Service Area Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d23614.736441096!2d146.40847!3d-41.2352!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xaa70b8f7a7b7c7d7%3A0x4045675218ce840!2sLatrobe%20TAS%207307%2C%20Australia!5e0!3m2!1sen!2sau!4v1703123456789!5m2!1sen!2sau"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="grayscale contrast-[1.1]"
                />
              </div>
              <div className="bg-muted/50 p-4">
                <p className="font-manrope text-sm text-muted-foreground">
                  <strong className="text-foreground">Base:</strong> Devonport &
                  Latrobe
                  <br />
                  <strong className="text-foreground">Coverage:</strong>{" "}
                  Shearwater to Penguin (and further for right clients)
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form
              onSubmit={handleSubmit}
              data-testid="inquiry-form"
              className="bg-white rounded-3xl border border-border p-6 md:p-8 space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-manrope">
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="input-name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    placeholder="John Smith"
                    className="h-12 rounded-xl border-ash-300 focus:border-charcoal-700 focus:ring-charcoal-700/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-manrope">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="input-email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    placeholder="john@example.com"
                    className="h-12 rounded-xl border-ash-300 focus:border-charcoal-700 focus:ring-charcoal-700/10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="font-manrope">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="input-phone"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    required
                    placeholder="0400 000 000"
                    className="h-12 rounded-xl border-ash-300 focus:border-charcoal-700 focus:ring-charcoal-700/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service" className="font-manrope">
                    Service Required
                  </Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, service_type: value })
                    }
                  >
                    <SelectTrigger
                      data-testid="select-service"
                      className="h-12 rounded-xl border-ash-300 focus:border-charcoal-700 focus:ring-charcoal-700/10"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal-care">Personal Care</SelectItem>
                      <SelectItem value="domestic">
                        Domestic Assistance
                      </SelectItem>
                      <SelectItem value="transport">
                        Transport & Community
                      </SelectItem>
                      <SelectItem value="social">Social Support</SelectItem>
                      <SelectItem value="respite">Respite Care</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="font-manrope">
                  Your Message *
                </Label>
                <Textarea
                  id="message"
                  data-testid="input-message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  required
                  placeholder="Tell me about your care needs and how I can help..."
                  rows={5}
                  className="rounded-xl border-ash-300 focus:border-charcoal-700 focus:ring-charcoal-700/10 resize-none"
                />
              </div>

              <Button
                type="submit"
                data-testid="submit-inquiry-btn"
                disabled={isSubmitting}
                className="w-full bg-sage-600 hover:bg-sage-700 text-white rounded-full h-14 text-lg font-manrope font-medium inquiry-pulse"
              >
                {isSubmitting ? "Sending..." : "Send Inquiry"}
              </Button>

              <p className="text-center font-manrope text-sm text-muted-foreground">
                I typically respond within 24 hours
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-cedar-800 text-white py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Logo & About */}
          <div>
            <img
              src={ASSETS.logo}
              alt="McKenzie Private Care"
              className="h-16 w-auto mb-6 brightness-0 invert"
            />
            <p className="font-manrope text-white/70 leading-relaxed">
              Professional in-home support services on Tasmania's North West
              Coast. Respectful, reliable, person-centred care.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-playfair text-xl mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {["About", "Services", "Pricing", "FAQs", "Contact"].map(
                (link) => (
                  <li key={link}>
                    <a
                      href={`#${link.toLowerCase()}`}
                      className="font-manrope text-white/70 hover:text-kangaroo-300 transition-colors"
                    >
                      {link}
                    </a>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-playfair text-xl mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-kangaroo-400" />
                <a
                  href="tel:0456786124"
                  className="font-manrope text-white/70 hover:text-kangaroo-300 transition-colors"
                >
                  0456 786 124
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-kangaroo-400" />
                <a
                  href="mailto:mckenzieprivatecare@gmail.com"
                  className="font-manrope text-white/70 hover:text-kangaroo-300 transition-colors text-sm"
                >
                  mckenzieprivatecare@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-kangaroo-400 flex-shrink-0 mt-0.5" />
                <span className="font-manrope text-white/70">
                  Devonport & Latrobe,
                  <br />
                  North West Tasmania
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-manrope text-sm text-white/60">
            © {currentYear} McKenzie Private Care. All rights reserved.
          </p>
          <p className="font-manrope text-sm text-white/60">
            ABN Registered • Fully Insured • Police Checked
          </p>
        </div>
      </div>
    </footer>
  );
};

// How It Works Page
const HowItWorksPage = () => {
  const navigate = useNavigate();
  
  const steps = [
    {
      number: "01",
      title: "Get in Touch",
      description: "Reach out via phone, email, or the contact form. We'll have a friendly, no-pressure chat about your situation, the type of support you're looking for, and how often you need it."
    },
    {
      number: "02",
      title: "Funding & Suitability Check",
      description: "We'll confirm whether your support is private/out-of-pocket or NDIS self-managed. This helps ensure everything is clear, compliant, and the right fit from the start."
    },
    {
      number: "03",
      title: "Personalised Support Plan",
      description: "Support is tailored to your individual needs and preferences. This may include personal care, domestic assistance, social support, transport, or respite. We focus on consistency, reliability, and building genuine trust."
    },
    {
      number: "04",
      title: "Agreement & Start of Services",
      description: "Once details are confirmed, a simple service agreement is put in place outlining services, rates, and expectations. From there, support can begin at times that work best for you."
    },
    {
      number: "05",
      title: "Ongoing Care & Communication",
      description: "Care is flexible and can be adjusted as your needs change. Open communication is encouraged, and support is always delivered with respect, dignity, and professionalism."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800 font-manrope mb-8 transition-colors"
          >
            <ChevronDown className="w-4 h-4 rotate-90" />
            Back to Home
          </motion.button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <img
              src={ASSETS.logo}
              alt="McKenzie Private Care"
              className="h-20 w-auto mx-auto mb-6"
            />
            <span className="font-manrope text-sm tracking-widest uppercase text-walnut-600 mb-4 block">
              The Process
            </span>
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
              How It Works
            </h1>
            <p className="font-manrope text-muted-foreground text-lg max-w-2xl mx-auto">
              Getting support through McKenzie Private Care is simple, flexible, and centred around you.
            </p>
          </motion.div>

          {/* Steps */}
          <div className="space-y-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 md:gap-8"
              >
                {/* Number */}
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-charcoal-700 flex items-center justify-center">
                    <span className="font-playfair text-xl md:text-2xl text-white">
                      {step.number}
                    </span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="flex-1 pb-8 border-b border-ash-300 last:border-0">
                  <h2 className="font-playfair text-xl md:text-2xl text-foreground mb-3">
                    {step.title}
                  </h2>
                  <p className="font-manrope text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-16 text-center"
          >
            <div className="bg-kangaroo-200 rounded-3xl p-8 md:p-12">
              <h3 className="font-playfair text-2xl md:text-3xl text-cedar-800 mb-4">
                Ready to Get Started?
              </h3>
              <p className="font-manrope text-cedar-600 mb-6">
                Book a free, no-obligation consultation today.
              </p>
              <Button
                onClick={() => navigate("/")}
                className="bg-sage-700 hover:bg-sage-800 text-white rounded-full px-8 py-6 text-lg font-manrope font-medium sage-pulse"
              >
                Get in Touch
              </Button>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
};

// Main Home Component
const HomePage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <WhySectionApart />
        <ServicesSection />
        <PricingSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
